// src/encounter/wrap-up-note-lines.tsx
//
// PROD-2947 · frame v2 ONLY: the Wrap-up block's patient-facing items as NOTE LINES, inside the note's Wrap-up block
// (wrap-up-view.tsx `inNote`), to the 6 Oct note interaction contract. Switch OFF never reaches this file: the step
// keeps the DS `WrapUpInstructionsPanel` and `FollowUpDurationInput`, unchanged.
//
//  · Patient instructions (K6): empty → the label and ONE quiet "Add"; opened or written → the note's inline line (the
//    same engine as every narrative: plain multi-line text, saved on pause / blur / unmount / page hide, flushed by the
//    leave guard, never written after the provider's signature). No voice-note control and no "coming soon" action
//    (FV2-06). Read-only after the provider sign (rule 38): the text only, or "None recorded".
//  · Follow-up (K1 + a counter): the count on the DS `NumberSelector` and the unit as ONE closed pick list (the DS
//    `PickListChip`), replacing the DS duration control's unit radio group (ENC-L11: no radio inside a note block).
//    Read-only after the provider sign: "2 weeks" / "None".
// Composed from DS parts (NoteLine, Button, NumberSelector, PickListChip) and the note's inline line. Graduation ask for the DS:
// `FollowUpDurationInput` with a pick-list unit, and a note-line variant of `WrapUpInstructionsPanel`.
import { useEffect, useRef, useState } from 'react';
import { NoteLine, NumberSelector, PickListChip, type AuthorRole } from '@scripsteam/scrips-signal-ds';
import type { FollowUpUnit } from '@/types/wrap-up-api';
import { FOLLOW_UP_LABEL, followUpText, INSTRUCTIONS_LABEL, NONE_RECORDED, UNIT_OPTIONS } from './wrap-up-note-lines-model';
import type { WrapUpFollowUpProps } from './wrap-up-follow-up';
import { AddEntryButton } from './add-entry';
import { InlineNoteLine } from './structured-note/inline-note-line';
import { lineEditors, PATIENT_INSTRUCTIONS_EDITOR } from './structured-note/note-phrases';
import type { StructuredNotePhrases } from './structured-note/structured-note-section';
import { plainSegs, segsText } from './lib/note-line-delta';

const CHIP_15 = { textSize: 'body-15', targetSize: 24 } as const;
const QUIET = 'text-text-secondary';

export interface WrapUpInstructionsLineProps {
  value: string;
  /** The page's write (the wrap-up upsert). Absent → read-only (rule 38). */
  onChange?: (value: string) => void;
  saving?: boolean;
  /** Frozen or not yet loaded: read-only. */
  disabled?: boolean;
  /** PROD-3148: the note's phrase library — `.` opens the phrase menu here as in the note lines (plain phrases and,
   *  with `pieces`, phrases with chart pieces, inserted where `.` was typed). Absent → plain text only. */
  phrases?: StructuredNotePhrases;
  /** Whose line it is (the phrase menu offers what this author may write). Default physician. */
  author?: AuthorRole;
}

/** The line's id (its anchor, and its key with the leave guard). */
export const INSTRUCTIONS_LINE_ID = 'wrap-up-instructions';

/**
 * Patient instructions as a note line (K6). Empty: the label and ONE quiet "Add". Opened or written: the note's own
 * inline line (structured-note/inline-note-line.tsx, lane D) — multi-line plain text, written to the page's wrap-up
 * upsert on a pause, on blur, on unmount and on page hide, flushed by the leave guard before it decides, and NEVER
 * written after the provider's signature (a draft open at the freeze is held on screen with the reason, and the guard
 * asks before it is left behind). Integration, Codex round 2: one engine for every free-text line, so the instructions
 * get every rule the narratives have.
 */
export function WrapUpInstructionsLine({ value, onChange, disabled, phrases, author = 'physician' }: WrapUpInstructionsLineProps) {
  const writable = !!onChange && !disabled;
  const [opened, setOpened] = useState(false);
  // What this line last sent, until the page's record says the same (the wrap-up read refreshes after the save).
  const [sent, setSent] = useState<string | null>(null);
  if (sent !== null && sent.trim() === value.trim()) setSent(null);
  const shown = sent ?? value;
  const wrapRef = useRef<HTMLDivElement>(null);
  const editors = [...lineEditors('wrap-up', author, phrases?.discipline), PATIENT_INSTRUCTIONS_EDITOR];
  useEffect(() => {
    if (opened) wrapRef.current?.querySelector<HTMLElement>('textarea, [contenteditable="true"]')?.focus();
  }, [opened]);
  if (!shown.trim() && !opened) {
    return (
      <NoteLine label={INSTRUCTIONS_LABEL} align="center">
        {writable ? (
          <AddEntryButton aria-label="Add patient instructions" onClick={() => setOpened(true)}>
            Add
          </AddEntryButton>
        ) : (
          <span className={QUIET}>{NONE_RECORDED}</span>
        )}
      </NoteLine>
    );
  }
  return (
    <div
      ref={wrapRef}
      onBlur={(e) => {
        // Opened and left empty: it closes again, and nothing was written.
        if (!(e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget)) && !shown.trim()) setOpened(false);
      }}
    >
      <InlineNoteLine
        id={INSTRUCTIONS_LINE_ID}
        label={INSTRUCTIONS_LABEL}
        segs={plainSegs(shown)}
        onCommit={(segs) => {
          const text = segsText(segs).trim();
          setSent(text);
          onChange?.(text);
        }}
        readOnly={!writable}
        macros={phrases?.macros}
        editors={editors}
        author={author}
        pieces={phrases?.pieces}
        suggested={phrases?.suggestFor?.(editors)}
        chartReads={phrases?.chartReads}
        renderChartPiece={phrases?.renderChartPiece}
      />
    </div>
  );
}

export function WrapUpFollowUpLine({ count, unit, onChange, saving, disabled }: WrapUpFollowUpProps) {
  // `disabled` is the page's freeze (rule 38) or a record not yet loaded: no write either way, so the line reads.
  if (disabled) {
    return (
      <NoteLine label={FOLLOW_UP_LABEL} align="center">
        <span className={count > 0 ? 'font-semibold text-text-primary' : QUIET}>{followUpText(count, unit)}</span>
      </NoteLine>
    );
  }
  return (
    <NoteLine label={FOLLOW_UP_LABEL} align="center" valueClassName="flex flex-wrap items-center gap-2">
      <NumberSelector
        value={count}
        min={0}
        step={1}
        ariaLabel={FOLLOW_UP_LABEL}
        disabled={saving}
        onChange={(next) => onChange({ count: Math.max(0, Math.round(next)), unit })}
      />
      <PickListChip
        options={UNIT_OPTIONS}
        value={unit}
        onChange={(next) => onChange({ count, unit: next as FollowUpUnit })}
        readOnly={saving}
        aria-label="Follow-up unit"
        {...CHIP_15}
      />
    </NoteLine>
  );
}
